import { useCurrentProject } from 'src/providers/current-project.provider'
import {
  CurecallBanner,
  CurecallButton,
  ECurecallButtonAppearance,
  ECurecallSize,
  SwitchIcon,
} from '@honestica/curecall-ui'
import { TCurrentOrder, TPatientListRequest } from './patients.types'
import { TPatientListTab } from 'src/types/patient-list'
import { TItemsPerPage } from 'src/types/others'
import { TNavigationLocales } from 'src/types/locales'
import { twMerge } from 'src/utils/tw-merge.util'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { useSession } from 'src/providers/auth.provider'
import { getTranslatedLabel } from 'src/utils/locale.util'
import PageTitle from 'src/components/page-title/page-title.component'

export type Props = {
  error?: unknown
  onSelectTab: ({ tabId, body }: TPatientListRequest) => void
  patientListTabs: Array<TPatientListTab>
  currentTab: TPatientListTab | undefined
  currentOrder: TCurrentOrder | undefined
  isPatientListSpoiled: boolean
}

type TBannerProps = {
  error: unknown
  projectId: number
  userId: number
}

const NoPatientListsBanner = ({ error, projectId, userId }: TBannerProps) => {
  const { t } = useTranslation()
  Sentry.captureException(error, {
    extra: {
      context: 'No patient list tabs',
      project: projectId,
      user: userId,
    },
  })
  return (
    <CurecallBanner appearance="WARNING">
      {t('pages.patient-list-page.no-patient-list-tabs', {
        a: (chunk: any) => (
          <a href="mailto:hello@curecall.com" className="tw-cursor-pointer tw-text-primary">
            {chunk}
          </a>
        ),
      })}
    </CurecallBanner>
  )
}

export default function PatientListHeader({
  error,
  patientListTabs,
  onSelectTab,
  currentOrder,
  currentTab,
  isPatientListSpoiled,
}: Readonly<Props>) {
  const { i18n, t } = useTranslation()
  const locale = i18n.language as TNavigationLocales
  const { project } = useCurrentProject()
  const { currentUser } = useSession()

  const nonEmptyTabs = patientListTabs.filter(
    (patientListTab) => patientListTab.PatientListTabColumns.length > 0,
  )

  const getColumnOrder = (tab: TPatientListTab) => {
    return currentOrder &&
      tab.PatientListTabColumns.find((tabColumn) => tabColumn.PatientListColumn.name === currentOrder.column)
      ? {
          column: currentOrder.column,
          descOrder: currentOrder.descOrder,
        }
      : {
          column: tab.PatientListTabColumns[0]?.PatientListColumn.name,
          descOrder: true,
        }
  }

  const handleSelectTab = (tab: TPatientListTab) => {
    onSelectTab({
      tabId: tab.id,
      body: {
        pageNumber: 1,
        patientsPerPage: 50 as TItemsPerPage,
        orderBy: getColumnOrder(tab),
      },
    })
  }

  return (
    <>
      <div className="c-flex c-justify-between">
        <PageTitle>{t('pages.patient-list-page.title')}</PageTitle>
        {isPatientListSpoiled && (
          <CurecallButton
            appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
            size={ECurecallSize.SMALL}
            iconType={<SwitchIcon className="c-w-3.5 c-h-3.5" />}
            hasIconAtStart={true}
            onClick={() => window.location?.reload()}
          >
            {t('pages.patient-list-page.refresh')}
          </CurecallButton>
        )}
      </div>
      {error === 'no patient list tabs' ? (
        currentUser?.id && NoPatientListsBanner({ error, projectId: project.id, userId: currentUser?.id })
      ) : (
        <ul className="c-flex c-gap-8 c-cursor-pointer c-my-0 c-relative">
          {nonEmptyTabs.map((tab) => (
            <li
              className={twMerge(
                ' c-py-1 c-text-primary-201 c-text-[1rem]',
                currentTab?.id === tab.id && ' c-border-b-4 c-border-b-primary c-text-primary',
              )}
              key={tab.id}
              onClick={() => handleSelectTab(tab)}
              onMouseDown={() => handleSelectTab(tab)}
            >
              {getTranslatedLabel(locale, tab.intlName)}
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
